import React, { ReactElement, useState } from 'react';
import { Space } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from 'common/components/antd/Button/Button';
import { Form } from 'common/components/antd/Form/Form';
import { Input } from 'common/components/antd/Input/Input';
import { useEffectOnceHook } from 'common/hooks/use-effect-once-hook';
import { getFromLocalstorage } from 'common/utils/local-storage.utils';
import { EAuthFlagName } from 'domains/auth/constants/auth.constants';
import {
  authCreateRecoveryFlow,
  authLogoutFlow,
  authUpdateRecoveryFlow,
} from 'domains/auth/services/auth.service';
import { ROUTES_MAPPING } from 'navigation/constants/route.constants';
import { SCard, SWrapper } from './reset-password.page.style';

export const ResetPasswordPage = (): ReactElement => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailFromSearchParams = searchParams.get('email');
  const [isLoginFormSubmitLoading, setIsLoginFormSubmitLoading] = useState<boolean>(false);
  const isAuth: boolean = getFromLocalstorage(EAuthFlagName);

  useEffectOnceHook(() => {
    (async (): Promise<void> => {
      try {
        await authLogoutFlow(navigate, false);
      } catch (error) {
        console.error(error);
      }
    })();
  }, [isAuth]);

  const onFinishLoginHandler = async (values: any): Promise<void> => {
    try {
      setIsLoginFormSubmitLoading(true);

      const { email } = values;

      const recoveryFlowData = await authCreateRecoveryFlow();

      let csrfToken = '';
      const csrfTokenData = recoveryFlowData.ui.nodes.find(
        (node) => 'name' in node.attributes && node.attributes.name === 'csrf_token',
      );

      if ('value' in csrfTokenData!.attributes) {
        csrfToken = csrfTokenData?.attributes?.value || '';
      }

      await authUpdateRecoveryFlow({
        flow: recoveryFlowData.id,
        updateRecoveryFlowBody: {
          csrf_token: csrfToken,
          email,
          method: 'code',
        },
      });

      navigate(ROUTES_MAPPING.PUBLIC.CONFIRM_CODE, {
        state: {
          flowId: recoveryFlowData.id,
          email,
        },
      });
    } catch (error) {
      console.error(error);
      setIsLoginFormSubmitLoading(false);
    }
  };

  return (
    <SWrapper>
      <SCard>
        <Form form={form} dataTestId="reset_password_form" onFinish={onFinishLoginHandler}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Form.Item name="email" rules={[{ type: 'email' }]} initialValue={emailFromSearchParams}>
              <Input
                disabled={!!emailFromSearchParams}
                dataTestId="login_input"
                size="large"
                allowClear
                placeholder={t('auth.resetPassword.input.identifier')!}
              />
            </Form.Item>
            <Form.Item noStyle>
              <Button
                loading={isLoginFormSubmitLoading}
                type="primary"
                dataTestId="submit_button"
                htmlType="submit"
                size="large"
                block
              >
                <Trans i18nKey="auth.resetPassword.button.resetPassword" />
              </Button>
            </Form.Item>
          </Space>
        </Form>
      </SCard>
    </SWrapper>
  );
};

export default ResetPasswordPage;
